(() => {
  const selectors = {
    section: '[data-os-section]',
    groups: '[data-os-product-groups]',
    group: '[data-os-group]',
    card: '.os-variant-card',
    filter: '[data-os-filter]',
    options: '[data-os-filter-options]',
    trigger: '.os-filter__trigger',
    clear: '[data-os-filter-clear]',
    image: '.os-variant-card__image',
    popup: '.os-variant-card-popup',
    popupClose: '.os-variant-card-popup__overlay, .os-variant-card-popup__close',
    loadMore: '[data-os-load-more]',
    loading: '[data-os-loading]'
  };

  const splitValues = value => (value || '').split(',').map(item => item.trim()).filter(Boolean);
  const formatLabel = value => value.split(';')[0].replace(/^(material|feature):/i, '').replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase()).trim();
  const getSelected = filter => Array.from(filter.querySelectorAll('input:checked')).map(input => input.value);
  const matches = (values, selected) => !selected.length || values.some(value => selected.includes(value));
  // Feature filter is AND: a card must have EVERY selected feature.
  const matchesAll = (values, selected) => !selected.length || selected.every(value => values.includes(value));
  const populateFilter = (filter, values, labelMap = {}) => {
    const options = filter.querySelector(selectors.options);
    if (!options) return;
    options.replaceChildren(...values.map(value => {
      const label = document.createElement('label');
      const input = document.createElement('input');
      const text = document.createElement('span');
      label.className = 'os-filter__option';
      input.type = 'checkbox';
      input.value = value;
      text.textContent = labelMap[value] || formatLabel(value);
      label.append(input, text);
      return label;
    }));
  };

  const scanFilters = section => {
    const cards = Array.from(section.querySelectorAll(selectors.card));
    const values = { material: new Set(), color: new Set(), feature: new Set() };
    const materialLabels = {};

    cards.forEach(card => {
      const mat = card.dataset.material;
      if (mat) {
        values.material.add(mat);
        if (card.dataset.materialLabel) materialLabels[mat] = card.dataset.materialLabel;
      }
      splitValues(card.dataset.color).forEach(value => values.color.add(value));
      splitValues(card.dataset.features).forEach(value => values.feature.add(value));
    });

    section.querySelectorAll(selectors.filter).forEach(filter => {
      const type = filter.dataset.osFilter;
      const sorted = Array.from(values[type] || []).sort();
      populateFilter(filter, sorted, type === 'material' ? materialLabels : {});
    });
  };

  const applyFilters = section => {
    const selected = {};
    section.querySelectorAll(selectors.filter).forEach(filter => {
      selected[filter.dataset.osFilter] = getSelected(filter);
    });

    // Show the Clear control only when at least one filter is selected.
    const clear = section.querySelector(selectors.clear);
    if (clear) clear.hidden = !Object.values(selected).some(values => values.length);

    section.querySelectorAll(selectors.card).forEach(card => {
      const visible = matches(splitValues(card.dataset.material), selected.material || [])
        && matches(splitValues(card.dataset.color), selected.color || [])
        && matchesAll(splitValues(card.dataset.features), selected.feature || []);
      card.classList.toggle('is-hidden', !visible);
    });

    section.querySelectorAll(selectors.group).forEach(group => {
      const cards = Array.from(group.querySelectorAll(selectors.card));
      // Hide a group when it has no visible card — covers both filtered-out groups and
      // groups whose variants are all hidden (0 cards rendered), which would otherwise
      // leave an empty title with no swatches.
      const hasVisible = cards.some(card => !card.classList.contains('is-hidden'));
      group.classList.toggle('is-hidden', !hasVisible);
    });
  };

  const openPopup = image => {
    document.querySelectorAll(`${selectors.popup}.is-open`).forEach(closePopup);
    const card = image.closest(selectors.card);
    const popup = card?.querySelector(selectors.popup);
    if (!popup) return;
    popup.hidden = false;
    requestAnimationFrame(() => popup.classList.add('is-open'));
    document.body.classList.add('os-popup-open');
  };

  const closePopup = popup => {
    if (!popup) return;
    popup.classList.remove('is-open');
    document.body.classList.remove('os-popup-open');
    window.setTimeout(() => { popup.hidden = true; }, 250);
  };

  const appendPage = async (section, addedIds) => {
    const groups = section.querySelector(selectors.groups);
    const button = section.querySelector(selectors.loadMore);
    const loading = section.querySelector(selectors.loading);
    if (!groups) return;
    const nextPage = Number(groups.dataset.currentPage || 1) + 1;
    const totalPages = Number(groups.dataset.totalPages || 1);
    if (nextPage > totalPages) return;
    if (button) button.disabled = true;
    if (loading) {
      loading.textContent = 'Loading swatches…';
      loading.hidden = false;
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', section.dataset.sectionId);
      url.searchParams.set('page', nextPage);
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Unable to load more swatches');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGroups = doc.querySelectorAll(`${selectors.groups} ${selectors.group}`);
      if (!nextGroups.length) throw new Error('No more swatches found');
      nextGroups.forEach(group => groups.appendChild(group));
      groups.dataset.currentPage = String(nextPage);
      if (button) button.hidden = nextPage >= totalPages;
      scanFilters(section);
      applyFilters(section);
      // Re-apply added state to newly appended cards
      if (addedIds) addedIds.forEach(id => window.SwatchCart.applyAddedState(section, id));
    } catch (error) {
      if (loading) loading.textContent = error.message;
    } finally {
      if (button) button.disabled = false;
      if (loading && !loading.textContent) loading.hidden = true;
    }
  };

  const bindSection = section => {
    const addedIds = new Set();

    scanFilters(section);
    applyFilters(section);

    // Mark cart items as added on initial load
    window.SwatchCart.fetchCartVariantIds().then(ids => {
      ids.forEach(id => { addedIds.add(id); window.SwatchCart.applyAddedState(section, id); });
    });

    const groups = section.querySelector(selectors.groups);
    if (Number(groups?.dataset.totalPages || 1) <= 4) (async () => {
      while (Number(groups?.dataset.currentPage || 1) < Number(groups?.dataset.totalPages || 1)) await appendPage(section, addedIds);
    })();

    const doAdd = async (variantId, btn) => {
      if (btn) btn.disabled = true;
      try {
        await window.SwatchCart.addToCart(variantId);
        addedIds.add(Number(variantId));
        window.SwatchCart.applyAddedState(section, variantId);
        await window.SwatchCart.refreshCartUi();
      } catch (e) {
        const card = section.querySelector(`.os-variant-card[data-variant-id="${variantId}"]`);
        const msg = card && card.querySelector('.os-variant-card__message');
        if (msg) msg.textContent = e.message;
        if (btn) btn.disabled = false;
      }
    };

    section.addEventListener('change', event => {
      if (event.target.matches(`${selectors.filter} input`)) applyFilters(section);
    });

    section.addEventListener('click', event => {
      const trigger = event.target.closest(selectors.trigger);
      const image = event.target.closest(selectors.image);
      const popupClose = event.target.closest(selectors.popupClose);
      const loadMore = event.target.closest(selectors.loadMore);
      const addSwatch = event.target.closest('.os-add-swatch-cart');
      const dl = event.target.closest('.os-download-specs');
      const clear = event.target.closest(selectors.clear);

      if (clear) {
        section.querySelectorAll(`${selectors.filter} input:checked`).forEach(input => { input.checked = false; });
        applyFilters(section);
        return;
      }
      if (dl && dl.dataset.specUrl) {
        window.open(dl.dataset.specUrl, '_blank', 'noopener');
        return;
      }
      if (addSwatch && !addSwatch.disabled) {
        doAdd(addSwatch.dataset.variantId, addSwatch);
        return;
      }
      if (popupClose) closePopup(popupClose.closest(selectors.popup));
      // os-atc click bubbles here — opens popup (no longer ATC)
      if (image) openPopup(image);
      if (loadMore) appendPage(section, addedIds);
    });

    section.addEventListener('mouseenter', event => {
      event.target.closest?.(selectors.filter)?.querySelector(selectors.trigger)?.setAttribute('aria-expanded', 'true');
    }, true);

    section.addEventListener('mouseleave', event => {
      event.target.closest?.(selectors.filter)?.querySelector(selectors.trigger)?.setAttribute('aria-expanded', 'false');
    }, true);

    section.addEventListener('click', event => {
      const icon = event.target.closest('.os-feature-icon');
      if (!icon) return;
      const tooltip = icon.querySelector('.os-feature-tooltip');
      if (tooltip) tooltip.setAttribute('aria-hidden', String(tooltip.getAttribute('aria-hidden') !== 'true'));
    });

    section.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        const openPopupElement = section.querySelector(`${selectors.popup}.is-open`);
        if (openPopupElement) closePopup(openPopupElement);
        return;
      }

      if (event.key !== 'Enter' && event.key !== ' ') return;
      const image = event.target.closest(selectors.image);
      if (image) {
        event.preventDefault();
        openPopup(image);
        return;
      }

      const icon = event.target.closest('.os-feature-icon');
      if (!icon) return;
      event.preventDefault();
      icon.click();
    });

    section.addEventListener('mouseenter', event => {
      event.target.closest?.('.os-feature-icon')?.querySelector('.os-feature-tooltip')?.setAttribute('aria-hidden', 'false');
    }, true);

    section.addEventListener('mouseleave', event => {
      event.target.closest?.('.os-feature-icon')?.querySelector('.os-feature-tooltip')?.setAttribute('aria-hidden', 'true');
    }, true);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(selectors.section).forEach(bindSection);
  });
})();
